import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Rnd } from "react-rnd";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { FORMATS } from "@/lib/formats";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Download, Type, Square, Circle, Image as ImageIcon, Sparkles,
  Wand2, Trash2, Copy, ChevronUp, ChevronDown, Loader2, Palette, Layers, X, Heading,
  Undo2, Redo2, AlignHorizontalJustifyCenter, AlignVerticalJustifyCenter, Check,
  QrCode, Minus, Triangle, Star, Search,
} from "lucide-react";

const FONTS = ["Outfit", "Manrope", "Poppins", "Cascadia Code", "Playfair Display", "Bebas Neue", "Montserrat", "Oswald", "Lobster", "Roboto Mono"];
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [scale, setScale] = useState(0.4);
  const [saving, setSaving] = useState(false);
  const [kit, setKit] = useState(null);
  const [aiCopyOpen, setAiCopyOpen] = useState(false);
  const [aiImgOpen, setAiImgOpen] = useState(false);
  const [guides, setGuides] = useState({ v: false, h: false });
  const [autoSaved, setAutoSaved] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [rightTab, setRightTab] = useState("properties");

  const histRef = useRef([]);      // undo stack (JSON snapshots)
  const futRef = useRef([]);       // redo stack
  const lastSnapRef = useRef(null);// last committed snapshot
  const restoringRef = useRef(false);
  const canvasRef = useRef(null);
  const stageWrapRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/designs/${id}`).then((r) => setDesign(r.data)).catch(() => { toast.error("Design not found"); navigate("/dashboard"); });
    api.get("/brandkit").then((r) => setKit(r.data)).catch(() => {});
  }, [id, navigate]);

  const recalcScale = useCallback(() => {
    if (!design || !stageWrapRef.current) return;
    const pad = 80;
    const aw = stageWrapRef.current.clientWidth - pad;
    const ah = stageWrapRef.current.clientHeight - pad;
    setScale(Math.min(aw / design.width, ah / design.height, 1));
  }, [design]);

  useEffect(() => {
    recalcScale();
    window.addEventListener("resize", recalcScale);
    return () => window.removeEventListener("resize", recalcScale);
  }, [recalcScale]);

  // ---- undo / redo ----
  const syncHist = () => { setCanUndo(histRef.current.length > 0); setCanRedo(futRef.current.length > 0); };
  const undo = useCallback(() => {
    if (!histRef.current.length) return;
    const snap = histRef.current.pop();
    if (lastSnapRef.current) futRef.current.push(lastSnapRef.current);
    restoringRef.current = true;
    setDesign((d) => ({ ...d, ...JSON.parse(snap) }));
    setSelectedId(null);
    syncHist();
  }, []);
  const redo = useCallback(() => {
    if (!futRef.current.length) return;
    const snap = futRef.current.pop();
    if (lastSnapRef.current) histRef.current.push(lastSnapRef.current);
    restoringRef.current = true;
    setDesign((d) => ({ ...d, ...JSON.parse(snap) }));
    setSelectedId(null);
    syncHist();
  }, []);

  // record history whenever the design changes (ignores restores from undo/redo)
  useEffect(() => {
    if (!design) return;
    const snap = JSON.stringify({ name: design.name, width: design.width, height: design.height, background: design.background, elements: design.elements });
    if (restoringRef.current) { restoringRef.current = false; lastSnapRef.current = snap; return; }
    if (lastSnapRef.current !== null && lastSnapRef.current !== snap) {
      histRef.current.push(lastSnapRef.current);
      if (histRef.current.length > 80) histRef.current.shift();
      futRef.current = [];
      setCanUndo(true); setCanRedo(false);
    }
    lastSnapRef.current = snap;
  }, [design]);

  const selected = design?.elements.find((e) => e.id === selectedId) || null;

  const update = (patch) => setDesign((d) => ({ ...d, ...patch }));
  const updateEl = (elId, patch) =>
    setDesign((d) => ({ ...d, elements: d.elements.map((e) => (e.id === elId ? { ...e, ...patch } : e)) }));

  const addEl = (el) => {
    const full = { opacity: 1, rotation: 0, ...el, id: el.id || uid() };
    setDesign((d) => ({ ...d, elements: [...d.elements, full] }));
    setSelectedId(full.id);
  };

  const cx = () => (design ? design.width / 2 : 540);

  const addText = (preset) => {
    const presets = {
      heading: { text: "Add a heading", fontSize: 90, fontWeight: "bold", width: 700, height: 130, fontFamily: "Bebas Neue" },
      sub: { text: "Add a subheading", fontSize: 54, fontWeight: "600", width: 640, height: 80, fontFamily: "Poppins" },
      body: { text: "Add a little bit of body text", fontSize: 34, fontWeight: "normal", width: 560, height: 60, fontFamily: "Poppins" },
    }[preset];
    addEl({
      type: "text", x: cx() - presets.width / 2, y: 200, ...presets,
      fontStyle: "normal", color: "#232323", textAlign: "center", letterSpacing: 0, lineHeight: 1.15,
    });
  };

  const addShape = (type) => {
    if (type === "line") return addEl({ type: "line", x: cx() - 250, y: 400, width: 500, height: 10, fill: "#DB960D", borderRadius: 6 });
    return addEl({ type, x: cx() - 150, y: 300, width: 300, height: 300, fill: "#DB960D", borderRadius: 0, borderWidth: 0, borderColor: "#232323" });
  };

  const insertQR = async (value) => {
    try {
      const url = await QRCode.toDataURL(value, { margin: 1, width: 512, color: { dark: "#232323", light: "#00000000" } });
      addEl({ type: "image", src: url, x: cx() - 150, y: 300, width: 300, height: 300, objectFit: "contain", borderRadius: 0, qrValue: value, qrDark: "#232323", qrLight: "#FFFFFF", qrTransparent: true });
      setQrOpen(false);
    } catch (e) { toast.error("Could not generate QR code"); }
  };

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = design.width * 0.6;
        const ratio = img.height / img.width;
        const w = Math.min(img.width, maxW);
        addEl({ type: "image", src: reader.result, x: cx() - w / 2, y: 200, width: w, height: w * ratio, objectFit: "cover", borderRadius: 0 });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const insertLogo = () => {
    if (!kit?.logo) { toast.info("Upload a logo in Brand Kit first"); return; }
    addEl({ type: "image", src: kit.logo, x: cx() - 200, y: 80, width: 400, height: 160, objectFit: "contain", borderRadius: 0 });
  };

  const removeEl = (elId) => { setDesign((d) => ({ ...d, elements: d.elements.filter((e) => e.id !== elId) })); setSelectedId(null); };
  const duplicateEl = (el) => addEl({ ...el, id: undefined, x: el.x + 30, y: el.y + 30 });

  const reorder = (elId, dir) => {
    setDesign((d) => {
      const arr = [...d.elements];
      const i = arr.findIndex((e) => e.id === elId);
      if (dir === "front") { const [it] = arr.splice(i, 1); arr.push(it); }
      else if (dir === "back") { const [it] = arr.splice(i, 1); arr.unshift(it); }
      else if (dir === "up" && i < arr.length - 1) { [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; }
      else if (dir === "down" && i > 0) { [arr[i], arr[i - 1]] = [arr[i - 1], arr[i]]; }
      return { ...d, elements: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { thumbnail, ...rest } = design;
      await api.put(`/designs/${id}`, { name: rest.name, width: rest.width, height: rest.height, background: rest.background, elements: rest.elements });
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const doExport = async (fmt) => {
    setExportOpen(false);
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 60));
    const el = canvasRef.current;
    const prev = el.style.transform;
    el.style.transform = "none";
    try {
      const canvas = await html2canvas(el, { backgroundColor: fmt === "jpg" ? "#ffffff" : null, scale: 2, useCORS: true, width: design.width, height: design.height, logging: false });
      const base = (design.name || "kann-design").replace(/\s+/g, "-").toLowerCase();
      if (fmt === "pdf") {
        const img = canvas.toDataURL("image/jpeg", 0.95);
        const pdf = new jsPDF({ orientation: design.width >= design.height ? "landscape" : "portrait", unit: "px", format: [design.width, design.height] });
        pdf.addImage(img, "JPEG", 0, 0, design.width, design.height);
        pdf.save(`${base}.pdf`);
      } else {
        const link = document.createElement("a");
        link.download = `${base}.${fmt}`;
        link.href = canvas.toDataURL(fmt === "jpg" ? "image/jpeg" : "image/png", 0.95);
        link.click();
      }
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
    finally { el.style.transform = prev; }
  };
  const exportPng = () => doExport("png");

  const exportAllFormats = async () => {
    setExportOpen(false);
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 60));
    const el = canvasRef.current;
    const prev = el.style.transform;
    el.style.transform = "none";
    try {
      const src = await html2canvas(el, { backgroundColor: design.background || "#ffffff", scale: 2, useCORS: true, width: design.width, height: design.height, logging: false });
      const img = src.toDataURL("image/jpeg", 0.92);
      const f0 = FORMATS[0];
      const pdf = new jsPDF({ unit: "px", format: [f0.w, f0.h], orientation: f0.w >= f0.h ? "landscape" : "portrait" });
      FORMATS.forEach((f, i) => {
        if (i > 0) pdf.addPage([f.w, f.h], f.w >= f.h ? "landscape" : "portrait");
        pdf.setFillColor(255, 255, 255); pdf.rect(0, 0, f.w, f.h, "F");
        const sc = Math.min(f.w / design.width, f.h / design.height);
        const w = design.width * sc, h = design.height * sc;
        pdf.addImage(img, "JPEG", (f.w - w) / 2, (f.h - h) / 2, w, h);
        pdf.setFontSize(11); pdf.setTextColor(150); pdf.text(`${f.label} · ${f.w}×${f.h}`, 10, 20);
      });
      pdf.save(`${(design.name || "kann-design").replace(/\s+/g, "-").toLowerCase()}-all-formats.pdf`);
      toast.success(`Exported ${FORMATS.length} formats`);
    } catch (e) { toast.error("Export failed"); }
    finally { el.style.transform = prev; }
  };

  const centerH = () => { if (selected) updateEl(selected.id, { x: Math.round((design.width - selected.width) / 2) }); };
  const centerV = () => { if (selected) updateEl(selected.id, { y: Math.round((design.height - selected.height) / 2) }); };

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target.isContentEditable;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); return; }
      if (typing) return;
      if (mod && e.key.toLowerCase() === "d" && selected) { e.preventDefault(); duplicateEl(selected); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selected) { e.preventDefault(); removeEl(selected.id); return; }
      if (e.key === "Escape") { setSelectedId(null); return; }
      if (selected && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        const step = e.shiftKey ? 20 : 2;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        updateEl(selected.id, { x: selected.x + dx, y: selected.y + dy });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, undo, redo]);

  // silent auto-save (debounced)
  useEffect(() => {
    if (!design) return;
    setAutoSaved(false);
    const t = setTimeout(async () => {
      try {
        await api.put(`/designs/${id}`, { name: design.name, width: design.width, height: design.height, background: design.background, elements: design.elements });
        setAutoSaved(true);
      } catch (err) { console.error("Auto-save failed:", err); }
    }, 2500);
    return () => clearTimeout(t);
  }, [design, id]);

  if (!design) return <div className="h-screen flex items-center justify-center bg-kann-base"><Loader2 className="w-8 h-8 text-kann-gold animate-spin" /></div>;

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-kann-base text-kann-text-pri">
      {/* Top bar */}
      <header className="h-14 shrink-0 bg-kann-surface border-b border-kann-border/30 flex items-center px-4 gap-3">
        <button onClick={() => navigate("/dashboard")} data-testid="editor-back-btn" className="p-2 rounded-md hover:bg-kann-surface-active transition-colors"><ArrowLeft className="w-4 h-4" /></button>
        <span className="logo-font text-xl text-kann-text-pri hidden sm:block">KANN<span className="text-kann-gold">.</span></span>
        <input value={design.name} onChange={(e) => update({ name: e.target.value })} data-testid="design-name-input"
          className="bg-transparent border border-transparent hover:border-kann-border/40 focus:border-kann-gold rounded-md px-3 py-1.5 text-sm outline-none w-28 sm:w-56 flex-1 sm:flex-none min-w-0" />
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1 mr-1">
            <button onClick={undo} disabled={!canUndo} data-testid="undo-btn" title="Undo (Ctrl+Z)" className="p-2 rounded-md hover:bg-kann-surface-active disabled:opacity-30 transition-colors"><Undo2 className="w-4 h-4" /></button>
            <button onClick={redo} disabled={!canRedo} data-testid="redo-btn" title="Redo (Ctrl+Shift+Z)" className="p-2 rounded-md hover:bg-kann-surface-active disabled:opacity-30 transition-colors"><Redo2 className="w-4 h-4" /></button>
          </div>
          <span data-testid="autosave-indicator" className={`hidden md:flex items-center gap-1 text-xs mr-1 transition-opacity ${autoSaved ? "text-kann-text-sec opacity-100" : "opacity-0"}`}><Check className="w-3.5 h-3.5" /> Saved</span>
          <div className="relative">
            <button onClick={() => setExportOpen((v) => !v)} data-testid="export-btn" className="border border-kann-border/50 rounded-full px-3 sm:px-4 py-2 text-sm hover:bg-kann-surface-active transition-colors flex items-center gap-2"><Download className="w-4 h-4" /> <span className="hidden sm:inline">Export</span> <ChevronDown className="w-3.5 h-3.5" /></button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 w-40 bg-kann-surface border border-kann-border/40 rounded-lg shadow-xl overflow-hidden" data-testid="export-menu">
                  <button onClick={() => doExport("png")} data-testid="export-png" className="w-full text-left px-4 py-2.5 text-sm hover:bg-kann-surface-active flex items-center gap-2"><ImageIcon className="w-4 h-4" /> PNG</button>
                  <button onClick={() => doExport("jpg")} data-testid="export-jpg" className="w-full text-left px-4 py-2.5 text-sm hover:bg-kann-surface-active flex items-center gap-2"><ImageIcon className="w-4 h-4" /> JPG</button>
                  <button onClick={() => doExport("pdf")} data-testid="export-pdf" className="w-full text-left px-4 py-2.5 text-sm hover:bg-kann-surface-active flex items-center gap-2"><Download className="w-4 h-4" /> PDF</button>
                  <button onClick={exportAllFormats} data-testid="export-all" className="w-full text-left px-4 py-2.5 text-sm hover:bg-kann-surface-active flex items-center gap-2 border-t border-kann-border/20"><Layers className="w-4 h-4" /> All formats (PDF)</button>
                </div>
              </>
            )}
          </div>
          <button onClick={save} disabled={saving} data-testid="save-design-btn" className="bg-kann-ink text-white font-semibold rounded-full px-3 sm:px-4 py-2 text-sm hover:bg-black transition-colors flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} <span className="hidden sm:inline">Save</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Left tools */}
        <aside className="w-full md:w-64 shrink-0 bg-kann-surface border-b md:border-b-0 md:border-r border-kann-border/30 overflow-y-auto p-3 space-y-4 max-h-[30vh] md:max-h-none" data-testid="editor-left-panel">
          <ToolGroup title="Text">
            <ToolBtn testid="tool-heading" icon={Heading} label="Heading" onClick={() => addText("heading")} />
            <ToolBtn testid="tool-subheading" icon={Type} label="Subheading" onClick={() => addText("sub")} />
            <ToolBtn testid="tool-body" icon={Type} label="Body" onClick={() => addText("body")} />
          </ToolGroup>
          <ToolGroup title="Elements">
            <ToolBtn testid="tool-rect" icon={Square} label="Rectangle" onClick={() => addShape("rect")} />
            <ToolBtn testid="tool-ellipse" icon={Circle} label="Ellipse" onClick={() => addShape("ellipse")} />
            <ToolBtn testid="tool-triangle" icon={Triangle} label="Triangle" onClick={() => addShape("triangle")} />
            <ToolBtn testid="tool-star" icon={Star} label="Star" onClick={() => addShape("star")} />
            <ToolBtn testid="tool-line" icon={Minus} label="Line" onClick={() => addShape("line")} />
            <ToolBtn testid="tool-qr" icon={QrCode} label="QR code" onClick={() => setQrOpen(true)} />
            <ToolBtn testid="tool-stock" icon={Search} label="Stock" onClick={() => setStockOpen(true)} />
            <ToolBtn testid="tool-upload" icon={ImageIcon} label="Upload" onClick={() => fileRef.current?.click()} />
          </ToolGroup>
          <ToolGroup title="AI Studio">
            <ToolBtn testid="tool-ai-image" icon={Wand2} label="AI image" gold onClick={() => setAiImgOpen(true)} />
            <ToolBtn testid="tool-ai-copy" icon={Sparkles} label="AI copy" gold onClick={() => setAiCopyOpen(true)} />
          </ToolGroup>
          <ToolGroup title="Brand">
            <ToolBtn testid="tool-insert-logo" icon={ImageIcon} label="Insert logo" onClick={insertLogo} />
            {(kit?.colors || []).length > 0 && (
              <div className="col-span-2 flex flex-wrap gap-1.5 px-0.5 pt-1">
                {(kit?.colors || []).map((c, i) => (
                  <button key={i} title={c} onClick={() => (selected ? updateEl(selected.id, selected.type === "text" ? { color: c } : { fill: c }) : update({ background: c }))}
                    className="w-6 h-6 rounded border border-kann-border/40 transition-transform hover:scale-110" style={{ background: c }} data-testid={`brand-color-${i}`} />
                ))}
              </div>
            )}
          </ToolGroup>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </aside>

        {/* Canvas */}
        <div ref={stageWrapRef} className="flex-1 min-h-0 min-w-0 dot-canvas overflow-auto flex items-center justify-center p-4 md:p-10 relative" onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }} data-testid="editor-stage">
          <div className="glow-orb" style={{ width: 520, height: 520, top: "18%", left: "38%" }} />
          <div style={{ width: design.width * scale, height: design.height * scale, position: "relative", flexShrink: 0, boxShadow: "0 24px 60px -20px rgba(0,0,0,0.25)" }}>
            <div ref={canvasRef} data-testid="editor-canvas"
              style={{ width: design.width, height: design.height, background: design.background, transform: `scale(${scale})`, transformOrigin: "top left", position: "absolute", top: 0, left: 0, overflow: "hidden" }}>
              {design.elements.map((el) => (
                <Rnd key={el.id} scale={scale} bounds="parent"
                  size={{ width: el.width, height: el.height }} position={{ x: el.x, y: el.y }}
                  onDrag={(e, d) => {
                    const th = 6 / scale;
                    setGuides({
                      v: Math.abs((d.x + el.width / 2) - design.width / 2) < th,
                      h: Math.abs((d.y + el.height / 2) - design.height / 2) < th,
                    });
                  }}
                  onDragStop={(e, d) => {
                    const th = 6 / scale; let x = d.x, y = d.y;
                    if (Math.abs((x + el.width / 2) - design.width / 2) < th) x = (design.width - el.width) / 2;
                    if (Math.abs((y + el.height / 2) - design.height / 2) < th) y = (design.height - el.height) / 2;
                    setGuides({ v: false, h: false });
                    updateEl(el.id, { x, y });
                  }}
                  onResizeStop={(e, dir, ref, delta, pos) => updateEl(el.id, { width: parseFloat(ref.style.width), height: parseFloat(ref.style.height), x: pos.x, y: pos.y })}
                  onMouseDown={() => setSelectedId(el.id)}
                  style={{ outline: selectedId === el.id ? "2px solid #DB960D" : "none", outlineOffset: 0, zIndex: selectedId === el.id ? 999 : 1 }}
                  enableResizing={true}>
                  <ElementView el={el} onEdit={(text) => updateEl(el.id, { text })} editable={selectedId === el.id && el.type === "text"} />
                </Rnd>
              ))}
              {guides.v && <div style={{ position: "absolute", top: 0, bottom: 0, left: design.width / 2, width: 1, background: "#DB960D", zIndex: 1000, pointerEvents: "none" }} />}
              {guides.h && <div style={{ position: "absolute", left: 0, right: 0, top: design.height / 2, height: 1, background: "#DB960D", zIndex: 1000, pointerEvents: "none" }} />}
            </div>
          </div>
        </div>

        {/* Right properties + layers */}
        <aside className="w-full md:w-72 shrink-0 bg-kann-surface border-t md:border-t-0 md:border-l border-kann-border/30 flex flex-col overflow-hidden max-h-[34vh] md:max-h-none" data-testid="editor-right-panel">
          <div className="flex shrink-0 border-b border-kann-border/30">
            <button onClick={() => setRightTab("properties")} data-testid="tab-properties"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-[11px] tracking-[0.2em] uppercase font-mono transition-colors ${rightTab === "properties" ? "text-kann-text-pri border-b-2 border-kann-gold bg-kann-base/50" : "text-kann-text-sec hover:text-kann-text-pri border-b-2 border-transparent"}`}>
              <Palette className="w-3.5 h-3.5 text-kann-gold" /> Properties
            </button>
            <button onClick={() => setRightTab("layers")} data-testid="tab-layers"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-[11px] tracking-[0.2em] uppercase font-mono transition-colors ${rightTab === "layers" ? "text-kann-text-pri border-b-2 border-kann-gold bg-kann-base/50" : "text-kann-text-sec hover:text-kann-text-pri border-b-2 border-transparent"}`}>
              <Layers className="w-3.5 h-3.5 text-kann-gold" /> Layers
              {design.elements.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-kann-surface-active text-kann-text-sec">{design.elements.length}</span>}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {rightTab === "properties" ? (
              <>
                <div className="p-4">
                  {selected ? <Properties el={selected} update={(p) => updateEl(selected.id, p)} /> : <CanvasProps design={design} update={update} />}
                </div>
                {selected && (
                  <div className="p-4 border-t border-kann-border/20 flex flex-wrap gap-2">
                    <MiniBtn testid="align-center-h" icon={AlignHorizontalJustifyCenter} label="Center H" onClick={centerH} />
                    <MiniBtn testid="align-center-v" icon={AlignVerticalJustifyCenter} label="Center V" onClick={centerV} />
                    <MiniBtn testid="layer-front" icon={ChevronUp} label="Front" onClick={() => reorder(selected.id, "front")} />
                    <MiniBtn testid="layer-back" icon={ChevronDown} label="Back" onClick={() => reorder(selected.id, "back")} />
                    <MiniBtn testid="layer-duplicate" icon={Copy} label="Duplicate" onClick={() => duplicateEl(selected)} />
                    <MiniBtn testid="layer-delete" icon={Trash2} label="Delete" danger onClick={() => removeEl(selected.id)} />
                  </div>
                )}
              </>
            ) : (
              <div className="p-4">
                <div className="space-y-1" data-testid="layers-list">
                  {[...design.elements].reverse().map((el) => (
                    <button key={el.id} onClick={() => setSelectedId(el.id)} data-testid={`layer-item-${el.id}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${selectedId === el.id ? "bg-kann-ink text-white" : "text-kann-text-sec hover:bg-kann-surface-active"}`}>
                      {el.type === "text" ? <Type className="w-3.5 h-3.5" /> : el.type === "image" ? <ImageIcon className="w-3.5 h-3.5" /> : el.type === "ellipse" ? <Circle className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                      <span className="truncate">{el.type === "text" ? el.text : el.qrValue ? "QR code" : el.type}</span>
                    </button>
                  ))}
                  {design.elements.length === 0 && <p className="text-xs text-kann-text-sec px-1">No elements yet. Add from the left panel.</p>}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {aiCopyOpen && <AICopyModal onClose={() => setAiCopyOpen(false)} onInsert={(text) => { addText("body"); setDesign((d) => { const els = [...d.elements]; els[els.length - 1] = { ...els[els.length - 1], text, width: 800, height: 200, fontSize: 40 }; return { ...d, elements: els }; }); setAiCopyOpen(false); }} />}
      {aiImgOpen && <AIImageModal onClose={() => setAiImgOpen(false)} onInsert={(src) => { addEl({ type: "image", src, x: cx() - 300, y: 200, width: 600, height: 600, objectFit: "cover", borderRadius: 0 }); setAiImgOpen(false); }} />}
      {qrOpen && <QRModal onClose={() => setQrOpen(false)} onInsert={insertQR} />}
      {stockOpen && <StockModal onClose={() => setStockOpen(false)} onInsert={(src) => { addEl({ type: "image", src, x: cx() - 300, y: 200, width: 600, height: 600, objectFit: "cover", borderRadius: 0 }); setStockOpen(false); }} />}
    </div>
  );
}

/* ---------------- element render ---------------- */
function shapeClip(type) {
  if (type === "triangle") return "polygon(50% 0%, 0% 100%, 100% 100%)";
  if (type === "star") return "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)";
  return "none";
}
function imgFilter(f) {
  if (!f) return "none";
  return `brightness(${f.brightness ?? 100}%) contrast(${f.contrast ?? 100}%) saturate(${f.saturate ?? 100}%) grayscale(${f.grayscale ?? 0}%) blur(${f.blur ?? 0}px)`;
}
function ElementView({ el, editable, onEdit }) {
  const base = { width: "100%", height: "100%", opacity: el.opacity ?? 1 };
  const shadow = el.shadow ? `0 6px ${el.shadowBlur ?? 16}px ${el.shadowColor || "rgba(0,0,0,0.35)"}` : "none";
  if (el.type === "text") {
    const grad = !!el.gradient;
    const common = { ...base, fontSize: el.fontSize, fontWeight: el.fontWeight, fontStyle: el.fontStyle, textAlign: el.textAlign, fontFamily: el.fontFamily, letterSpacing: el.letterSpacing, lineHeight: el.lineHeight || 1.15, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "visible", cursor: "move" };
    if (editable)
      return <textarea value={el.text} onChange={(e) => onEdit(e.target.value)} onMouseDown={(e) => e.stopPropagation()}
        style={{ ...common, color: el.color, background: "transparent", border: "none", outline: "none", resize: "none", padding: 0 }} />;
    const style = {
      ...common, whiteSpace: "pre-wrap",
      color: grad ? "transparent" : el.color,
      backgroundImage: grad ? `linear-gradient(${el.gradAngle ?? 135}deg, ${el.color}, ${el.fill2 || "#DB960D"})` : undefined,
      WebkitBackgroundClip: grad ? "text" : undefined, backgroundClip: grad ? "text" : undefined,
      WebkitTextFillColor: grad ? "transparent" : undefined,
      WebkitTextStroke: el.strokeWidth ? `${el.strokeWidth}px ${el.strokeColor || "#232323"}` : undefined,
      textShadow: el.shadow ? `0 3px ${el.shadowBlur ?? 12}px ${el.shadowColor || "rgba(0,0,0,0.35)"}` : undefined,
    };
    return <div style={style}>{el.text}</div>;
  }
  if (el.type === "image")
    return <img src={el.src} alt="" draggable={false} style={{ ...base, objectFit: el.objectFit || "cover", borderRadius: el.borderRadius || 0, filter: imgFilter(el.filters), boxShadow: shadow, pointerEvents: "none" }} />;
  const clip = shapeClip(el.type);
  const bg = el.gradient ? `linear-gradient(${el.gradAngle ?? 135}deg, ${el.fill}, ${el.fill2 || "#232323"})` : el.fill;
  const shape = {
    ...base, background: bg,
    borderRadius: el.type === "ellipse" ? "50%" : (clip !== "none" ? 0 : el.borderRadius || 0),
    border: el.borderWidth && clip === "none" ? `${el.borderWidth}px solid ${el.borderColor}` : "none",
    clipPath: clip !== "none" ? clip : undefined,
    boxShadow: clip === "none" ? shadow : "none",
    cursor: "move",
  };
  return <div style={shape} />;
}

/* ---------------- properties ---------------- */
function Field({ label, children }) {
  return <div className="mb-3"><div className="text-[11px] tracking-wide uppercase text-kann-text-sec mb-1.5">{label}</div>{children}</div>;
}
const inputCls = "w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-2.5 py-1.5 text-sm outline-none";

function ColorRow({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" value={/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value || "") ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} data-testid={testid} className="w-9 h-9 rounded border border-kann-border/40 bg-transparent cursor-pointer" />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      </div>
    </Field>
  );
}

function Properties({ el, update }) {
  if (el.type === "text")
    return (
      <div className="mt-3">
        <Field label="Text"><textarea value={el.text} onChange={(e) => update({ text: e.target.value })} rows={2} className={inputCls} data-testid="prop-text" /></Field>
        <Field label="Font">
          <select value={el.fontFamily} onChange={(e) => update({ fontFamily: e.target.value })} className={inputCls} data-testid="prop-font">
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Size"><input type="number" value={el.fontSize} onChange={(e) => update({ fontSize: Number(e.target.value) })} className={inputCls} data-testid="prop-fontsize" /></Field>
          <Field label="Spacing"><input type="number" value={el.letterSpacing} onChange={(e) => update({ letterSpacing: Number(e.target.value) })} className={inputCls} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Weight">
            <select value={el.fontWeight} onChange={(e) => update({ fontWeight: e.target.value })} className={inputCls}>
              <option value="normal">Regular</option><option value="600">Medium</option><option value="bold">Bold</option>
            </select>
          </Field>
          <Field label="Style">
            <select value={el.fontStyle} onChange={(e) => update({ fontStyle: e.target.value })} className={inputCls}>
              <option value="normal">Normal</option><option value="italic">Italic</option>
            </select>
          </Field>
        </div>
        <Field label="Align">
          <div className="flex gap-1">
            {["left", "center", "right"].map((a) => (
              <button key={a} onClick={() => update({ textAlign: a })} className={`flex-1 py-1.5 rounded-full text-xs capitalize ${el.textAlign === a ? "bg-kann-ink text-white" : "border border-kann-border/40 text-kann-text-sec hover:bg-kann-surface-active"}`}>{a}</button>
            ))}
          </div>
        </Field>
        <ColorRow label="Color" value={el.color} onChange={(v) => update({ color: v })} testid="prop-color" />
        <GradientControls el={el} update={update} baseLabel="Gradient (2nd color)" />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Outline"><input type="number" min="0" value={el.strokeWidth || 0} onChange={(e) => update({ strokeWidth: Number(e.target.value) })} className={inputCls} data-testid="prop-stroke-width" /></Field>
          <ColorRow label="Outline color" value={el.strokeColor || "#232323"} onChange={(v) => update({ strokeColor: v })} />
        </div>
        <ShadowControls el={el} update={update} />
        <OpacityRow el={el} update={update} />
      </div>
    );
  if (el.type === "image")
    return (
      <div className="mt-3">
        {el.qrValue && <QRColorControls el={el} update={update} />}
        <Field label="Fit">
          <select value={el.objectFit} onChange={(e) => update({ objectFit: e.target.value })} className={inputCls}>
            <option value="cover">Cover</option><option value="contain">Contain</option><option value="fill">Fill</option>
          </select>
        </Field>
        <Field label="Corner radius"><input type="number" value={el.borderRadius} onChange={(e) => update({ borderRadius: Number(e.target.value) })} className={inputCls} /></Field>
        <FilterControls el={el} update={update} />
        <ShadowControls el={el} update={update} />
        <OpacityRow el={el} update={update} />
      </div>
    );
  return (
    <div className="mt-3">
      <ColorRow label="Fill" value={el.fill} onChange={(v) => update({ fill: v })} testid="prop-fill" />
      <GradientControls el={el} update={update} baseLabel="Gradient (2nd color)" />
      {el.type === "rect" && <Field label="Corner radius"><input type="number" value={el.borderRadius} onChange={(e) => update({ borderRadius: Number(e.target.value) })} className={inputCls} /></Field>}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Border width"><input type="number" value={el.borderWidth} onChange={(e) => update({ borderWidth: Number(e.target.value) })} className={inputCls} /></Field>
        <ColorRow label="Border" value={el.borderColor} onChange={(v) => update({ borderColor: v })} />
      </div>
      <ShadowControls el={el} update={update} />
      <OpacityRow el={el} update={update} />
    </div>
  );
}

function QRColorControls({ el, update }) {
  const [busy, setBusy] = useState(false);
  const dark = el.qrDark || "#232323";
  const light = el.qrLight || "#FFFFFF";
  const transparent = el.qrTransparent ?? true;
  const regen = async (patch) => {
    const next = { qrDark: dark, qrLight: light, qrTransparent: transparent, ...patch };
    setBusy(true);
    try {
      const bg = next.qrTransparent ? "#00000000" : next.qrLight;
      const url = await QRCode.toDataURL(el.qrValue, { margin: 1, width: 512, color: { dark: next.qrDark, light: bg } });
      update({ src: url, ...next });
    } catch (_) { toast.error("Could not update QR code"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-3 border-b border-kann-border/20 pb-3">
      <div className="flex items-center gap-2 text-[11px] tracking-wide uppercase text-kann-text-sec mb-2">
        <QrCode className="w-3.5 h-3.5 text-kann-gold" /> QR code {busy && <Loader2 className="w-3 h-3 animate-spin" />}
      </div>
      <ColorRow label="QR color" value={dark} onChange={(v) => regen({ qrDark: v })} testid="qr-prop-dark" />
      <label className="flex items-center gap-2 text-[11px] tracking-wide uppercase text-kann-text-sec mb-2 cursor-pointer">
        <input type="checkbox" checked={transparent} onChange={(e) => regen({ qrTransparent: e.target.checked })} data-testid="qr-prop-transparent" className="accent-kann-gold" /> Transparent background
      </label>
      {!transparent && <ColorRow label="Background" value={light} onChange={(v) => regen({ qrLight: v })} testid="qr-prop-light" />}
    </div>
  );
}

function ShadowControls({ el, update }) {
  return (
    <div className="mb-3 border-t border-kann-border/20 pt-3">
      <label className="flex items-center gap-2 text-[11px] tracking-wide uppercase text-kann-text-sec mb-2 cursor-pointer">
        <input type="checkbox" checked={!!el.shadow} onChange={(e) => update({ shadow: e.target.checked })} data-testid="prop-shadow-toggle" className="accent-kann-gold" /> Drop shadow
      </label>
      {el.shadow && (
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Blur ${el.shadowBlur ?? 16}`}><input type="range" min="0" max="60" value={el.shadowBlur ?? 16} onChange={(e) => update({ shadowBlur: Number(e.target.value) })} className="w-full accent-kann-gold" /></Field>
          <ColorRow label="Shadow" value={el.shadowColor || "#000000"} onChange={(v) => update({ shadowColor: v })} />
        </div>
      )}
    </div>
  );
}

function GradientControls({ el, update, baseLabel }) {
  return (
    <div className="mb-3">
      <label className="flex items-center gap-2 text-[11px] tracking-wide uppercase text-kann-text-sec mb-2 cursor-pointer">
        <input type="checkbox" checked={!!el.gradient} onChange={(e) => update({ gradient: e.target.checked })} data-testid="prop-gradient-toggle" className="accent-kann-gold" /> Gradient
      </label>
      {el.gradient && (
        <>
          <ColorRow label={baseLabel} value={el.fill2 || "#232323"} onChange={(v) => update({ fill2: v })} testid="prop-gradient-color2" />
          <Field label={`Angle ${el.gradAngle ?? 135}°`}><input type="range" min="0" max="360" value={el.gradAngle ?? 135} onChange={(e) => update({ gradAngle: Number(e.target.value) })} className="w-full accent-kann-gold" /></Field>
        </>
      )}
    </div>
  );
}

function FilterControls({ el, update }) {
  const f = el.filters || {};
  const set = (k, v) => update({ filters: { ...f, [k]: v } });
  const rows = [["Brightness", "brightness", 0, 200, 100], ["Contrast", "contrast", 0, 200, 100], ["Saturation", "saturate", 0, 200, 100], ["Grayscale", "grayscale", 0, 100, 0], ["Blur", "blur", 0, 20, 0]];
  return (
    <div className="mb-3 border-t border-kann-border/20 pt-3">
      <div className="text-[11px] tracking-wide uppercase text-kann-text-sec mb-2">Filters</div>
      {rows.map(([label, key, min, max, def]) => (
        <Field key={key} label={`${label} ${f[key] ?? def}${key === "blur" ? "px" : "%"}`}>
          <input type="range" min={min} max={max} value={f[key] ?? def} onChange={(e) => set(key, Number(e.target.value))} className="w-full accent-kann-gold" data-testid={`prop-filter-${key}`} />
        </Field>
      ))}
      <button onClick={() => update({ filters: {} })} className="text-xs text-kann-text-sec hover:text-kann-text-pri underline">Reset filters</button>
    </div>
  );
}

function OpacityRow({ el, update }) {
  return (
    <Field label={`Opacity ${Math.round((el.opacity ?? 1) * 100)}%`}>
      <input type="range" min="0" max="1" step="0.05" value={el.opacity ?? 1} onChange={(e) => update({ opacity: Number(e.target.value) })} className="w-full accent-kann-gold" />
    </Field>
  );
}

function CanvasProps({ design, update }) {
  const currentFmt = FORMATS.find((f) => f.w === design.width && f.h === design.height)?.id || "";
  return (
    <div className="mt-3">
      <Field label="Social format">
        <select className={inputCls} data-testid="canvas-format" value={currentFmt}
          onChange={(e) => { const f = FORMATS.find((x) => x.id === e.target.value); if (f) update({ width: f.w, height: f.h }); }}>
          <option value="">Custom ({design.width}×{design.height})</option>
          {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label} · {f.w}×{f.h}</option>)}
        </select>
      </Field>
      <ColorRow label="Background" value={design.background} onChange={(v) => update({ background: v })} testid="prop-bg" />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Width"><input type="number" value={design.width} onChange={(e) => update({ width: Number(e.target.value) })} className={inputCls} /></Field>
        <Field label="Height"><input type="number" value={design.height} onChange={(e) => update({ height: Number(e.target.value) })} className={inputCls} /></Field>
      </div>
      <p className="text-xs text-kann-text-sec">Select an element to edit its properties.</p>
    </div>
  );
}

/* ---------------- small UI ---------------- */
function ToolGroup({ title, children }) {
  return <div><div className="text-[11px] tracking-[0.2em] uppercase text-kann-text-sec font-mono mb-2 px-1">{title}</div><div className="grid grid-cols-2 gap-1.5">{children}</div></div>;
}
function ToolBtn({ icon: Icon, label, onClick, gold, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex flex-col items-center justify-center gap-1.5 px-2 py-3 rounded-lg text-[11px] leading-tight text-center transition-colors active:scale-[0.97] ${gold ? "bg-kann-base border border-kann-gold/40 text-kann-gold hover:bg-kann-ink hover:text-white" : "border border-kann-border/30 text-kann-text-pri hover:bg-kann-surface-active hover:border-kann-border/50"}`}>
      <Icon className="w-4 h-4 shrink-0" /> {label}
    </button>
  );
}
function MiniBtn({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex-1 min-w-[70px] flex flex-col items-center gap-1 py-2 rounded-md text-[11px] transition-colors ${danger ? "text-red-400 hover:bg-red-500/10" : "text-kann-text-sec hover:bg-kann-surface-active hover:text-kann-text-pri"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
function SectionLabel({ icon: Icon, children }) {
  return <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-kann-text-sec font-mono"><Icon className="w-3.5 h-3.5 text-kann-gold" /> {children}</div>;
}

/* ---------------- AI modals ---------------- */
function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60" onMouseDown={onClose}>
      <div className="w-full max-w-md backdrop-blur-xl bg-kann-surface/95 border border-kann-border/40 rounded-xl p-6" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-kann-text-pri">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-kann-surface-active"><X className="w-4 h-4 text-kann-text-sec" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StockModal({ onClose, onInsert }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inserting, setInserting] = useState(null);
  const [sources, setSources] = useState({ unsplash: true, pexels: true });
  const [searched, setSearched] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true); setSearched(true);
    try {
      const { data } = await api.get("/stock/search", { params: { q: q.trim() } });
      setResults(data.results || []);
      setSources(data.sources || {});
    } catch (_) { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  const pick = async (item) => {
    setInserting(item.id);
    try {
      const { data } = await api.get("/stock/download", { params: { url: item.full } });
      onInsert(data.data_url);
    } catch (_) { toast.error("Could not add image"); setInserting(null); }
  };

  const noKeys = searched && !sources.unsplash && !sources.pexels;

  return (
    <ModalShell title="Stock photos" onClose={onClose}>
      <form onSubmit={search} className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="stock-search-input"
          placeholder="Search Unsplash & Pexels…" className="flex-1 bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold px-3 py-2 text-sm outline-none" />
        <button type="submit" data-testid="stock-search-btn" className="px-4 py-2 rounded-md bg-kann-ink text-white text-sm font-semibold hover:bg-black flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
        </button>
      </form>
      {noKeys && (
        <div className="mt-4 text-sm text-kann-text-sec bg-kann-base border border-kann-border/30 rounded-md p-3" data-testid="stock-no-keys">
          Stock search is not configured yet. Add <span className="font-mono text-kann-text-pri">UNSPLASH_ACCESS_KEY</span> and/or <span className="font-mono text-kann-text-pri">PEXELS_API_KEY</span> to the backend environment to enable it.
        </div>
      )}
      <div className="mt-4 grid grid-cols-3 gap-2 max-h-[52vh] overflow-y-auto" data-testid="stock-results">
        {results.map((r) => (
          <button key={r.id} onClick={() => pick(r)} disabled={!!inserting} data-testid={`stock-item-${r.id}`}
            className="relative aspect-square rounded-md overflow-hidden border border-kann-border/30 group disabled:opacity-60">
            <img src={r.thumb} alt="" className="w-full h-full object-cover" />
            <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100">{r.author} · {r.source}</span>
            {inserting === r.id && <span className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="w-5 h-5 text-white animate-spin" /></span>}
          </button>
        ))}
        {searched && !loading && !noKeys && results.length === 0 && <p className="col-span-3 text-sm text-kann-text-sec py-6 text-center">No results.</p>}
      </div>
    </ModalShell>
  );
}

function QRModal({ onClose, onInsert }) {
  const [value, setValue] = useState("https://kannprint.com/");
  return (
    <ModalShell title="QR code" onClose={onClose}>
      <label className="block text-[11px] tracking-wide uppercase text-kann-text-sec mb-2">Link or text</label>
      <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="qr-input"
        className="w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold px-3 py-2 text-sm outline-none"
        placeholder="https://…" />
      <p className="text-xs text-kann-text-sec mt-2">Tip: link to your product page or interactive manual.</p>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-kann-border/50 hover:bg-kann-surface-active">Cancel</button>
        <button onClick={() => value.trim() && onInsert(value.trim())} data-testid="qr-insert-btn"
          className="px-4 py-2 rounded-full text-sm bg-kann-ink text-white font-semibold hover:bg-black flex items-center gap-2"><QrCode className="w-4 h-4" /> Add QR</button>
      </div>
    </ModalShell>
  );
}


function AICopyModal({ onClose, onInsert }) {
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("headline");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const gen = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setResult("");
    try { const { data } = await api.post("/ai/copy", { prompt, kind, tone: "confident" }); setResult(data.text); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Generate copy" onClose={onClose}>
      <div className="flex gap-2 mb-3 flex-wrap">
        {["headline", "tagline", "caption", "description"].map((k) => (
          <button key={k} onClick={() => setKind(k)} className={`px-3 py-1.5 rounded-full text-xs capitalize ${kind === k ? "bg-kann-ink text-white" : "border border-kann-border/40 text-kann-text-sec"}`}>{k}</button>
        ))}
      </div>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="What is this design about?" className={inputCls} data-testid="ai-copy-prompt" />
      <button onClick={gen} disabled={busy} data-testid="ai-copy-generate" className="mt-3 w-full bg-kann-ink text-white font-semibold rounded-full py-2.5 text-sm hover:bg-black transition-colors flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
      </button>
      {result && (
        <div className="mt-4">
          <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-sm text-kann-text-pri bg-kann-base rounded-md p-3 border border-kann-border/30" data-testid="ai-copy-result">{result}</div>
          <button onClick={() => onInsert(result)} data-testid="ai-copy-insert" className="mt-3 w-full border border-kann-gold/50 text-kann-gold rounded-full py-2.5 text-sm hover:bg-kann-ink hover:text-white transition-colors">Insert into design</button>
        </div>
      )}
    </ModalShell>
  );
}

function AIImageModal({ onClose, onInsert }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgs, setImgs] = useState([]);
  const gen = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setImgs([]);
    try { const { data } = await api.post("/ai/image", { prompt, count: 2 }); setImgs((data.images || [data]).map((x) => x.image)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Generate image" onClose={onClose}>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Describe the artwork, e.g. 'golden abstract geometric background'" className={inputCls} data-testid="ai-image-prompt" />
      <button onClick={gen} disabled={busy} data-testid="ai-image-generate" className="mt-3 w-full bg-kann-ink text-white font-semibold rounded-full py-2.5 text-sm hover:bg-black transition-colors flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {busy ? "Generating 2…" : "Generate"}
      </button>
      {imgs.length > 0 && (
        <div className="mt-4">
          <p className="text-xs text-kann-text-sec mb-2">Click a variation to insert it:</p>
          <div className={`grid ${imgs.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
            {imgs.map((src, i) => (
              <button key={i} onClick={() => onInsert(src)} data-testid={`ai-image-insert-${i}`}
                className="rounded-md border border-kann-border/30 overflow-hidden hover:ring-2 hover:ring-kann-gold transition-all">
                <img src={src} alt={`variation ${i + 1}`} className="w-full aspect-square object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </ModalShell>
  );
}
